import { useState } from "react";
import { copy } from "../copy";
import { sourceShort, topicTitle } from "../domain/display";
import { columnOverdue, FLOW_COLUMNS, hoursSince, scoreLabel, stageAgeLabel, stageEnteredAt, topicsInColumn, type FlowColumnId } from "../domain/flow";
import { activeTaskForTopic, taskChip } from "../domain/tasks";
import { topicsForAccount } from "../domain/selectors";
import { useApp } from "../state/store";
import type { Topic } from "../types";

export function Kanban({ readonly = false, topics: given }: { readonly?: boolean; topics?: Topic[] }) {
  const app = useApp();
  const topics = given ?? topicsForAccount(app.state.topics, app.accountId);
  const now = new Date();
  return (
    <div className="stack">
      <div className="flow-board">
        {FLOW_COLUMNS.map((column) => {
          const cards = topicsInColumn(topics, column.id);
          return (
            <section key={column.id} className="flow-column" aria-label={column.id}>
              <header>
                <h2>{column.id}</h2>
                <span className="muted num">{cards.length}</span>
              </header>
              {cards.length === 0 ? <p className="muted">{copy.flow.emptyColumn}</p> : null}
              {cards.map((topic) => (
                <FlowCard key={topic.id} topic={topic} column={column.id} now={now} readonly={readonly} />
              ))}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function FlowCard({ topic, column, now, readonly }: { topic: Topic; column: FlowColumnId; now: Date; readonly: boolean }) {
  const app = useApp();
  const entered = stageEnteredAt(topic, app.state.activities);
  const hours = hoursSince(entered, now);
  const overdue = columnOverdue(column, hours);
  const [returning, setReturning] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const assistant = sourceShort(topic) || copy.flow.noAssistant;
  const linked = activeTaskForTopic(app.state.tasks ?? [], topic);
  const chip = linked ? taskChip(linked) : null;

  function decide(status: "已确认" | "不做") {
    const previous = topic.status;
    app.setStatus(topic.id, status);
    const message = status === "已确认" ? copy.status.confirmed(topicTitle(topic)) : copy.status.skipped(topicTitle(topic));
    app.notify(message, "info", { label: copy.undo, undo: { topicId: topic.id, status: previous } });
  }

  async function sendBack() {
    if (!reason.trim()) {
      setError(copy.flow.reasonNeeded);
      return;
    }
    setBusy(true);
    setError("");
    try {
      await app.returnForRewrite(topic.id, reason);
      setReturning(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : copy.flow.reasonNeeded);
    } finally {
      setBusy(false);
    }
  }

  const overdueLabel = column === "待确认" ? "待确认超过 48 小时" : "待审阅超过 24 小时";
  return (
    <article className="flow-card">
      <h3>{topicTitle(topic)}</h3>
      <p className="muted">{assistant}</p>
      {topic.status !== column ? <p className="muted">{topic.status}</p> : null}
      <p className={overdue ? "is-late" : "muted"} title={overdue ? overdueLabel : undefined}>{copy.flow.stage} {stageAgeLabel(entered, now)}</p>
      <p>{scoreLabel(app.state.postScores ?? [], topic.id)}</p>
      {chip ? <p className="task-chip">{chip}</p> : null}
      {readonly ? null : (
      <div className="row-actions">
        {column === "待确认" ? (
          <>
            <button type="button" className="button line" onClick={() => decide("已确认")}>{copy.doIt}</button>
            <button type="button" className="button ghost" onClick={() => decide("不做")}>{copy.skip}</button>
          </>
        ) : null}
        {column === "待审阅" ? (
          <>
            <button type="button" className="button line" onClick={() => void app.approveEdited(topic.id, null)}>{copy.flow.pass}</button>
            <button type="button" className="button ghost" onClick={() => setReturning((value) => !value)}>{copy.flow.returnReason}</button>
          </>
        ) : null}
      </div>
      )}
      {!readonly && returning ? (
        <label className="flow-reason">
          <span>{copy.flow.comment}</span>
          <textarea value={reason} rows={3} maxLength={500} onChange={(event) => setReason(event.target.value)} />
          {error ? <small className="inline-error">{error}</small> : null}
          <button type="button" className="button line" disabled={busy} onClick={() => void sendBack()}>{copy.flow.confirmReturn}</button>
        </label>
      ) : null}
    </article>
  );
}
